import React, { useContext, useState, useEffect } from "react"

import {
	Paper,
	Container,
	Grid,
	Typography,
	TextField,
	Button,
	Box,
	CircularProgress,
} from "@material-ui/core"
import Autocomplete from "@material-ui/lab/Autocomplete"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { startOfMonth, format } from "date-fns"
import {
	BackspaceOutlined,
	HourglassEmpty,
	CheckCircleOutline,
	Memory,
	EmojiPeople,
	Event,
	EventAvailable,
	AssignmentTurnedIn,
	Search,
	Forum,
	HowToReg,
	People,
	ChevronRight,
} from "@material-ui/icons"

import { AuthContext } from "../../context/Auth/AuthContext"
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext"
import api from "../../services/api"
import PeriodFilter from "../../components/PeriodFilter"

import Chart from "./Chart"
import LupaRH from "../../components/LupaRH"
import { ondasMar } from "../../utils/estiloNavy"

// Paleta verde dos cards. O âmbar fica, porque "em espera" e afins precisam
// continuar chamando atenção.
const CORES = {
	escuro: { verde: "#2dd4bf", verdeClaro: "#34d399", agua: "#22d3ee", ambar: "#f5b23d" },
	claro: { verde: "#0d9488", verdeClaro: "#059669", agua: "#0891b2", ambar: "#d97706" },
}

const hexParaRgba = (hex, alfa) => {
	const n = parseInt(hex.slice(1), 16)
	return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alfa})`
}

// Número que sobe do zero até o valor. Aceita "61%" / "0.7%" também.
const ContadorAnimado = ({ valor }) => {
	const texto = String(valor)
	const m = texto.match(/^(-?\d+(?:[.,]\d+)?)(.*)$/)
	const alvo = m ? parseFloat(m[1].replace(",", ".")) : null
	const casas = m && /[.,]/.test(m[1]) ? m[1].split(/[.,]/)[1].length : 0
	const sufixo = m ? m[2] : ""
	const [atual, setAtual] = useState(0)

	useEffect(() => {
		if (alvo === null) return undefined
		let frame
		const inicio = performance.now()
		const duracao = 900
		const passo = agora => {
			const k = Math.min((agora - inicio) / duracao, 1)
			setAtual(alvo * (1 - Math.pow(1 - k, 3)))
			if (k < 1) frame = requestAnimationFrame(passo)
		}
		frame = requestAnimationFrame(passo)
		return () => cancelAnimationFrame(frame)
	}, [alvo])

	if (alvo === null) return texto
	return `${atual.toFixed(casas)}${sufixo}`
}

// Onda decorativa no rodapé de cada card
// Onda do rodape dos cartoes: dois periodos lado a lado deslizando devagar
// (o desenho se repete a cada 400, entao o loop nao "pula").
const Onda = ({ cor, atraso = 0 }) => (
	<svg
		viewBox="0 0 800 60"
		preserveAspectRatio="none"
		aria-hidden="true"
		style={{
			position: "absolute", left: 0, bottom: 0, width: "200%", height: 46, pointerEvents: "none",
			animation: `dbOnda 9s linear ${atraso}s infinite`,
		}}
	>
		<path d="M0 34 Q100 14 200 34 T400 34 T600 34 T800 34 L800 60 L0 60 Z" fill={cor} />
	</svg>
)

const getDefaultPeriod = () => ({
	dateFrom: format(startOfMonth(new Date()), "yyyy-MM-dd"),
	dateTo: format(new Date(), "yyyy-MM-dd"),
	label: "Este mês",
})

// Curriculos desenhados no banner (a lupa passa por cima deles)
const CURRICULOS = [
	{ x: 0, y: 18, r: -9, cor: "#f59e0b" },
	{ x: 78, y: 0, r: 0, cor: "#14b8a6", selo: true },
	{ x: 156, y: 18, r: 9, cor: "#38bdf8" },
];

const useStyles = makeStyles(theme => {
	const escuro = theme.palette.type === "dark"

	// fundo dos painéis: escuro = verde-quase-preto; claro = branco
	const painel = escuro
		? {
				backgroundColor: "#121c1b",
				backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0))",
				border: "1px solid rgba(148,163,184,0.14)",
				boxShadow: "0 8px 24px rgba(0,0,0,.35)",
			}
		: {
				backgroundColor: "#ffffff",
				border: "1px solid #e1eeec",
				boxShadow: "0 4px 14px rgba(10,38,36,.06)",
			}

	return {
		// O contentBody do layout e overflow:hidden — as outras paginas resolvem
		// isso pelo MainContainer, que cria a propria area de rolagem. O Dashboard
		// nao usa MainContainer, entao precisa criar a dele aqui: sem isto, tudo
		// que passa da altura da tela fica cortado e nao ha o que rolar.
		pagina: {
			height: "100%",
			overflowY: "auto",
			overflowX: "hidden",
			...theme.scrollbarStyles,
		},
		container: {
			paddingTop: theme.spacing(3),
			paddingBottom: theme.spacing(4),
		},

		// ── Banner "Central de comando" (estilo Multi Tech: azul-noite com
		// verde, nos dois temas) com animacoes leves ──
		hero: {
			position: "relative",
			overflow: "hidden",
			borderRadius: 20,
			padding: "26px 28px",
			marginBottom: theme.spacing(3),
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			flexWrap: "wrap",
			gap: 20,
			minHeight: 210,
			...ondasMar("rgba(255,255,255,0.09)", "rgba(255,255,255,0.13)"),
			background:
				"radial-gradient(420px 220px at 88% 30%, rgba(255,255,255,.08), transparent 70%), linear-gradient(120deg, #0b5e58 0%, #0d7a72 55%, #0f8f84 100%)",
			border: "none",
			boxShadow: escuro
				? "0 14px 40px rgba(0,0,0,.45), 0 0 0 1px rgba(20,184,166,.05)"
				: "0 14px 34px rgba(15,23,42,.22)",
		},
		heroTexto: { position: "relative", zIndex: 1, animation: "iaSubir .5s ease both" },
		// barquinho a vela navegando nas ondas do banner
		barco: {
			position: "absolute",
			bottom: 8,
			left: "76%",
			width: 34,
			height: 34,
			zIndex: 0,
			pointerEvents: "none",
			animation: "iaBarcoViaja 26s ease-in-out infinite alternate",
			"& svg": { width: "100%", height: "100%", animation: "iaBarco 3.2s ease-in-out infinite", transformOrigin: "50% 90%" },
			[theme.breakpoints.down("sm")]: { display: "none" },
		},
		// bolhas subindo devagar no banner (efeito de mar, bem discreto)
		bolha: {
			position: "absolute",
			bottom: 10,
			borderRadius: "50%",
			border: "1px solid rgba(255,255,255,.45)",
			background: "rgba(255,255,255,.08)",
			pointerEvents: "none",
			zIndex: 0,
			animation: "iaBolha 9s ease-in infinite",
			[theme.breakpoints.down("sm")]: { display: "none" },
		},
		heroPilula: {
			display: "inline-flex",
			alignItems: "center",
			gap: 8,
			padding: "5px 12px",
			borderRadius: 20,
			fontSize: "0.68rem",
			fontWeight: 700,
			letterSpacing: "0.08em",
			color: "#e2e8f0",
			background: "rgba(255,255,255,.06)",
			border: "1px solid rgba(255,255,255,.14)",
			"&::before": {
				content: '""',
				width: 7,
				height: 7,
				borderRadius: "50%",
				background: "#14b8a6",
				boxShadow: "0 0 8px #14b8a6",
				animation: "sbPontoVivo 1.8s ease-in-out infinite",
			},
		},
		heroTitulo: {
			fontSize: "1.9rem",
			fontWeight: 800,
			color: "#ffffff",
			marginTop: 10,
			lineHeight: 1.15,
			"& span": { color: "#5eead4", textShadow: "0 0 18px rgba(45,212,191,.35)" },
		},
		heroSub: {
			fontSize: "0.88rem",
			color: "#a7d8d1",
			marginTop: 4,
		},
		// ilustracao: tres curriculos e uma lupa passando por eles
		ilustra: {
			position: "absolute",
			right: 48,
			top: "50%",
			width: 270,
			height: 170,
			marginTop: -92,
			pointerEvents: "none",
			zIndex: 1,
			[theme.breakpoints.down("md")]: { display: "none" },
		},
		curriculo: {
			position: "absolute",
			top: 0,
			width: 104,
			height: 136,
			padding: 12,
			borderRadius: 12,
			background: "#ffffff",
			boxShadow: "0 14px 30px rgba(0,0,0,.35)",
			display: "flex",
			flexDirection: "column",
			gap: 7,
			animation: "iaSubir .6s ease backwards",
		},
		cvFoto: {
			width: 28,
			height: 28,
			borderRadius: "50%",
			marginBottom: 4,
			opacity: 0.9,
		},
		cvLinha: {
			height: 6,
			borderRadius: 3,
			background: "#e2ecea",
		},
		cvSelo: {
			position: "absolute",
			top: -10,
			right: -10,
			width: 26,
			height: 26,
			borderRadius: "50%",
			background: "#14b8a6",
			color: "#fff",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			border: "3px solid #0d7a72",
			"& svg": { fontSize: 15 },
		},
		lupaHero: {
			position: "absolute",
			top: 34,
			left: 0,
			width: 96,
			height: 96,
			zIndex: 2,
			filter: "drop-shadow(0 8px 16px rgba(0,0,0,.45))",
			animation: "$lupaVarrer 7s ease-in-out infinite",
		},
		"@keyframes lupaVarrer": {
			"0%, 100%": { transform: "translate(-10px, 10px) rotate(-8deg)" },
			"30%": { transform: "translate(80px, -14px) rotate(0deg)" },
			"45%": { transform: "translate(80px, -14px) scale(1.06)" },
			"75%": { transform: "translate(170px, 12px) rotate(8deg)" },
		},

		// ── Funil de recrutamento ──
		funil: {
			...painel,
			borderRadius: 16,
			padding: theme.spacing(2.5, 3),
			display: "grid",
			gridTemplateColumns: "repeat(4, 1fr)",
			gap: 8,
			marginBottom: theme.spacing(1),
			animation: "iaSubir .5s ease backwards",
			[theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, 1fr)", rowGap: 20 },
		},
		etapa: {
			position: "relative",
			display: "flex",
			flexDirection: "column",
			gap: 6,
			paddingRight: 28,
		},
		etapaSeta: {
			position: "absolute",
			right: 0,
			top: 8,
			color: escuro ? "rgba(148,163,184,.45)" : "#b6cbc8",
			[theme.breakpoints.down("sm")]: { display: "none" },
		},
		etapaTopo: { display: "flex", alignItems: "center", gap: 10 },
		etapaIcone: {
			width: 36,
			height: 36,
			borderRadius: 10,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0,
			"& svg": { fontSize: 19 },
		},
		etapaNumero: { fontSize: "1.6rem", fontWeight: 800, lineHeight: 1 },
		etapaNome: { fontSize: "0.8rem", fontWeight: 700, color: theme.palette.text.primary },
		etapaDesc: { fontSize: "0.72rem", color: theme.palette.text.secondary, lineHeight: 1.3 },
		etapaTrilha: {
			height: 6,
			borderRadius: 4,
			marginTop: 4,
			backgroundColor: escuro ? "rgba(255,255,255,.06)" : "#eef4f3",
			overflow: "hidden",
		},
		etapaBarra: {
			height: "100%",
			borderRadius: 4,
			transition: "width .8s ease",
		},
		// filtros dentro do banner
		filtros: {
			position: "relative",
			zIndex: 1,
			display: "flex",
			alignItems: "center",
			flexWrap: "wrap",
			gap: 10,
			padding: 10,
			borderRadius: 14,
			// no claro o painel é branco sólido: os campos são brancos com texto
			// escuro e ficavam ilegíveis/transparentes sobre o verde
			background: escuro ? "rgba(0,0,0,.25)" : "#ffffff",
			border: `1px solid ${escuro ? "rgba(255,255,255,.08)" : "transparent"}`,
			boxShadow: escuro ? "none" : "0 6px 18px rgba(0,0,0,.12)",
		},

		// Lista suspensa curta e com rolagem propria: antes ela crescia com a
		// quantidade de atendentes e tomava a tela inteira.
		listaFiltro: {
			maxHeight: 240,
			padding: "4px 0",
			...theme.scrollbarStyles,
		},
		// O popup do Autocomplete nasce com a largura exata do campo. Como o campo
		// e estreito, "Todas as conexoes" quebrava em duas linhas e sobrava aquele
		// vazio embaixo. Aqui ele cresce conforme o maior nome da lista.
		popupFiltro: {
			width: "auto !important",
			minWidth: 210,
		},
		paperFiltro: {
			marginTop: 6,
			borderRadius: 10,
			border: `1px solid ${theme.palette.divider}`,
			boxShadow: escuro ? "0 10px 28px rgba(0,0,0,.45)" : "0 10px 28px rgba(16,24,40,.12)",
		},
		opcaoFiltro: {
			whiteSpace: "nowrap",
			fontSize: "0.875rem",
			minHeight: 36,
			padding: "6px 12px",
			// data-focus e a opcao sob o teclado/mouse; aria-selected e a atual.
			'&[data-focus="true"]': {
				backgroundColor: "rgba(13,148,136,0.08)",
			},
			'&[aria-selected="true"]': {
				backgroundColor: "rgba(13,148,136,0.16)",
				fontWeight: 600,
			},
		},
		semResultado: {
			fontSize: "0.85rem",
			color: theme.palette.text.secondary,
			padding: "10px 12px",
		},
		iconeBusca: {
			fontSize: "1.05rem",
			color: theme.palette.text.secondary,
			marginRight: 2,
		},
		formControl: {
			width: 210,
			"& .MuiOutlinedInput-root": {
				borderRadius: 8,
				fontSize: "0.875rem",
				backgroundColor: `${theme.palette.background.paper} !important`,
				paddingLeft: 10,
			},
			"& .MuiAutocomplete-input": {
				padding: "4px 4px !important",
			},
			// sem seta (forcePopupIcon={false}), o campo nao precisa do recuo que o
			// MUI reserva para ela
			"& .MuiAutocomplete-inputRoot": {
				paddingRight: "10px !important",
			},
			// o placeholder e o valor selecionado: precisa ser legivel, nao um
			// cinza apagado de "campo vazio"
			"& input::placeholder": {
				color: theme.palette.text.primary,
				opacity: 1,
			},
		},
		clearBtn: {
			height: 40,
			borderRadius: 8,
			textTransform: "none",
			fontWeight: 600,
			fontSize: "0.875rem",
			padding: "0 18px",
			color: escuro ? "#cbd5e1" : "#475467",
			backgroundColor: escuro ? "rgba(255,255,255,0.06)" : "#f1f3f5",
			"&:hover": { backgroundColor: escuro ? "rgba(255,255,255,0.12)" : "#e4e7ec" },
		},

		// título de seção com a barrinha verde à esquerda
		tituloSecao: {
			fontSize: "0.74rem",
			fontWeight: 800,
			textTransform: "uppercase",
			letterSpacing: "0.1em",
			color: escuro ? "#cbd5e1" : "#344054",
			margin: "22px 4px 12px",
			display: "flex",
			alignItems: "center",
			gap: 10,
			"&::before": {
				content: '""',
				width: 4,
				height: 16,
				borderRadius: 2,
				background: "#14b8a6",
				boxShadow: escuro ? "0 0 8px rgba(20,184,166,.6)" : "none",
			},
		},

		linhaBanco: {
			display: "flex",
			alignItems: "center",
			gap: 12,
			padding: "7px 0",
		},
		nomeBanco: {
			minWidth: 110,
			fontSize: "0.82rem",
			fontWeight: 600,
			textTransform: "capitalize",
		},
		trilhaBarra: {
			flex: 1,
			height: 8,
			borderRadius: 6,
			backgroundColor: "rgba(128,128,128,0.15)",
			overflow: "hidden",
		},
		barra: {
			height: "100%",
			borderRadius: 6,
			background: "linear-gradient(90deg, #0D9488, #14b8a6)",
		},
		valorBanco: {
			minWidth: 34,
			textAlign: "right",
			fontSize: "0.82rem",
			fontWeight: 700,
		},
		volumeBanco: {
			minWidth: 96,
			textAlign: "right",
			fontSize: "0.76rem",
			opacity: 0.65,
			fontWeight: 600,
		},
		cardGrid: {
			marginBottom: theme.spacing(2),
		},

		// ── Cards ──  (--cor e --brilho vêm do style de cada card)
		statCard: {
			...painel,
			position: "relative",
			overflow: "hidden",
			padding: theme.spacing(2.5, 3),
			paddingBottom: theme.spacing(3.5),
			borderRadius: 16,
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(0.5),
			animation: "iaSubir .5s ease backwards",
			transition: "transform .25s ease, box-shadow .25s ease",
			// linha colorida no topo
			"&::before": {
				content: '""',
				position: "absolute",
				top: 0,
				left: 0,
				right: 0,
				height: 3,
				background:
					"linear-gradient(90deg, var(--cor) 0%, var(--cor) 35%, rgba(255,255,255,.85) 50%, var(--cor) 65%, var(--cor) 100%)",
				backgroundSize: "250% 100%",
				animation: "dbLinha 4.5s ease-in-out infinite",
			},
			// brilho suave no canto
			"&::after": {
				content: '""',
				position: "absolute",
				inset: 0,
				background: "radial-gradient(220px 110px at 100% 0%, var(--brilho), transparent 70%)",
				pointerEvents: "none",
			},
			"&:hover $cardIconBox": {
				animation: "dbIconePop .45s ease",
			},
			"&:hover": {
				transform: "translateY(-4px)",
				boxShadow: escuro
					? "0 16px 36px rgba(0,0,0,.5), 0 0 24px var(--brilho)"
					: "0 14px 30px rgba(10,38,36,.12)",
			},
			"& > *": { position: "relative", zIndex: 1 },
		},
		cardHeader: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
		},
		cardTitle: {
			fontSize: "0.72rem",
			fontWeight: 700,
			textTransform: "uppercase",
			letterSpacing: "0.07em",
			color: theme.palette.text.secondary,
		},
		cardIconBox: {
			width: 40,
			height: 40,
			borderRadius: 11,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			// balanco leve e constante (cada cartao num tempo diferente)
			animation: "dbIconeBalanco 4s ease-in-out infinite",
		},
		cardNumber: {
			fontSize: "2.1rem",
			fontWeight: 800,
			lineHeight: 1.1,
			minHeight: 38,
			margin: "4px 0 2px",
			display: "flex",
			alignItems: "center",
			letterSpacing: "-0.01em",
		},
		cardLabel: {
			fontSize: "0.78rem",
			fontWeight: 500,
			color: theme.palette.text.secondary,
		},
		cardFooter: {
			fontSize: "0.75rem",
			color: escuro ? "#64748b" : "#98a2b3",
			marginTop: theme.spacing(0.5),
		},
		// card em destaque (fundo verde cheio), como o "Total"
		cardDestaque: {
			background: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%) !important",
			border: "none !important",
			boxShadow: "0 12px 30px rgba(20,184,166,.3) !important",
			"&::before": { display: "none" },
			// faixa de brilho que atravessa o cartao verde de tempos em tempos
			"&::after": {
				inset: "0 auto 0 0",
				width: "35%",
				background: "linear-gradient(90deg, transparent, rgba(255,255,255,.22), transparent)",
				animation: "sbVarrer 5s ease-in-out infinite",
			},
			"& $cardTitle, & $cardLabel, & $cardFooter": { color: "rgba(255,255,255,.85)" },
		},
		chartPaper: {
			...painel,
			padding: theme.spacing(3),
			borderRadius: 16,
			height: 300,
			display: "flex",
			overflow: "hidden",
			flexDirection: "column",
		},
		rankPaper: {
			...painel,
			padding: theme.spacing(2.5, 3),
			borderRadius: 16,
			maxHeight: 300,
			display: "flex",
			flexDirection: "column",
			overflowY: "auto",
		},
		paperTitle: {
			fontSize: "0.85rem",
			fontWeight: 700,
			color: theme.palette.text.secondary,
			marginBottom: theme.spacing(1.5),
		},
	}
})

const Dashboard = () => {
	const classes = useStyles()
	const escuro = useTheme().palette.type === "dark"
	const P = escuro ? CORES.escuro : CORES.claro

	const { user } = useContext(AuthContext)
	const { whatsApps } = useContext(WhatsAppsContext)

	const [usersList, setUsersList] = useState([])

	const [period, setPeriod] = useState(getDefaultPeriod())
	const [connectionFilter, setConnectionFilter] = useState("all")
	const [userFilter, setUserFilter] = useState("all")
	// O que esta digitado no campo, separado do que esta selecionado. Fica
	// vazio o tempo todo, menos enquanto a pessoa digita: assim o campo mostra
	// o filtro atual como placeholder em vez de texto de verdade — nada para
	// ficar selecionado em azul ao clicar.
	const [buscaConexao, setBuscaConexao] = useState("")
	const [buscaAtendente, setBuscaAtendente] = useState("")

	const [resumo, setResumo] = useState(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		const fetchData = async () => {
			try {
				const { data } = await api.get("/users", { params: { pageNumber: 1 } })
				setUsersList(data.users || [])
			} catch (err) {
				console.error(err)
			}
		}
		fetchData()
	}, [])

	useEffect(() => {
		let vivo = true

		const buscar = async () => {
			setLoading(true)
			try {
				const { data } = await api.get("/dashboard/resumo", {
					params: {
						dateFrom: period.dateFrom || undefined,
						dateTo: period.dateTo || undefined,
						userId: userFilter !== "all" ? userFilter : undefined,
						whatsappId: connectionFilter !== "all" ? connectionFilter : undefined,
					},
				})
				if (vivo) setResumo(data)
			} catch (err) {
				console.error(err)
			}
			if (vivo) setLoading(false)
		}

		buscar()
		return () => {
			vivo = false
		}
	}, [period, userFilter, connectionFilter])

	const handleClear = () => {
		setPeriod(getDefaultPeriod())
		setConnectionFilter("all")
		setUserFilter("all")
	}

	const t = resumo?.tickets || {}
	const ia = resumo?.ia || {}
	const ent = resumo?.entrevistas || {}
	const temVagas = (ent.porVaga?.length || 0) > 0

	const tituloGrafico = `Conversas com candidatos · ${period.label || "período"}`

	const cardsAtendimento = [
		{
			title: "Total",
			count: t.total ?? 0,
			label: "Candidatos",
			footer: `Ativos agora: ${(t.aberto ?? 0) + (t.espera ?? 0)}`,
			color: P.verde,
			icon: People,
			destaque: true,
		},
		{
			title: "Em espera",
			count: t.espera ?? 0,
			label: "Candidatos",
			footer: "Esperando o RH responder",
			color: P.ambar,
			icon: HourglassEmpty,
		},
		{
			title: "Em aberto",
			count: t.aberto ?? 0,
			label: "Candidatos",
			footer: "Em conversa com o RH",
			color: P.agua,
			icon: Forum,
		},
		{
			title: "Resolvidos",
			count: t.resolvido ?? 0,
			label: "Candidatos",
			footer: "Conversas finalizadas no período",
			color: P.verdeClaro,
			icon: HowToReg,
		},
	]

	const cardsIA = [
		{
			title: "Atendidos pela IA",
			count: ia.atendidos ?? 0,
			label: "Conversas",
			footer: "Conversas iniciadas no período",
			color: P.ambar,
			icon: Memory,
		},
		{
			title: "Resolvidos sozinha",
			count: ia.resolvidosPelaIA ?? 0,
			label: "Conversas",
			footer: "Sem precisar de pessoa",
			color: P.verdeClaro,
			icon: AssignmentTurnedIn,
		},
		{
			title: "Precisaram de humano",
			count: ia.precisaramHumano ?? 0,
			label: "Conversas",
			footer: "Transferidas para o RH",
			color: P.agua,
			icon: EmojiPeople,
		},
		{
			title: "Taxa de resolução",
			count: `${ia.taxaResolucao ?? 0}%`,
			label: "Da IA",
			footer: "Quanto ela fecha sem ajuda",
			color: P.verde,
			icon: CheckCircleOutline,
		},
	]

	const cardsEntrevistas = [
		{
			title: "Agendadas",
			count: ent.agendadas ?? 0,
			label: "Entrevistas",
			footer: `${ent.agendadasPelaIA ?? 0} pela assistente`,
			color: P.verde,
			icon: Event,
		},
		{
			title: "Comparecimento",
			count: `${ent.taxaComparecimento ?? 0}%`,
			label: "Dos que tiveram resultado",
			footer: `${ent.compareceram ?? 0} vieram · ${ent.faltaram ?? 0} faltaram`,
			color: P.agua,
			icon: CheckCircleOutline,
		},
		{
			title: "Próximos 7 dias",
			count: ent.proximos7Dias ?? 0,
			label: "Entrevistas",
			footer: "Já marcadas na agenda",
			color: P.ambar,
			icon: HourglassEmpty,
		},
		{
			title: "Horários livres",
			count: ent.horariosLivres ?? 0,
			label: "Na agenda",
			footer: "Disponíveis para a assistente oferecer",
			color: P.verdeClaro,
			icon: EventAvailable,
		},
	]

	// Caminho do candidato: chamou no WhatsApp -> IA fez a triagem ->
	// entrevista marcada -> compareceu.
	const funil = [
		{ nome: "Conversas", desc: "Candidatos que chamaram no WhatsApp", valor: t.total ?? 0, cor: P.agua, icon: Forum },
		{ nome: "Triados pela IA", desc: `${ia.elegiveis ?? 0} se encaixaram na vaga`, valor: ia.triados ?? 0, cor: P.verde, icon: Memory },
		{ nome: "Entrevistas agendadas", desc: `${ent.agendadasPelaIA ?? 0} marcadas pela IA`, valor: ent.agendadas ?? 0, cor: P.verdeClaro, icon: Event },
		{ nome: "Compareceram", desc: `${ent.faltaram ?? 0} faltaram`, valor: ent.compareceram ?? 0, cor: P.ambar, icon: HowToReg },
	]

	// Os filtros viraram campos de busca: com dezenas de atendentes, rolar uma
	// lista inteira e pior do que digitar as tres primeiras letras. A opcao
	// "todos" entra como primeiro item da lista, no lugar do MenuItem fixo.
	const opcoesConexao = [
		{ id: "all", name: "Todas as conexões" },
		...(whatsApps || []).map(w => ({ id: w.id, name: w.name })),
	]
	const opcoesAtendente = [
		{ id: "all", name: "Todos os atendentes" },
		...(usersList || []).map(u => ({ id: u.id, name: u.name })),
	]
	const acharOpcao = (lista, id) =>
		lista.find(o => String(o.id) === String(id)) || lista[0]

	const primeiroNome = user.name?.split(" ")[0] || "Comandante"

	return (
		<div className={classes.pagina}>
			<Container maxWidth="lg" className={classes.container}>
				{/* Banner do RH com os filtros */}
				<div className={classes.hero}>
					{[
						{ l: "52%", s: 10, d: 0 },
						{ l: "60%", s: 6, d: 2.5 },
						{ l: "68%", s: 8, d: 5 },
						{ l: "45%", s: 5, d: 6.5 },
					].map((b, i) => (
						<span
							key={i}
							className={classes.bolha}
							style={{ left: b.l, width: b.s, height: b.s, animationDelay: `${b.d}s` }}
						/>
					))}
					<span className={classes.barco} aria-hidden="true">
						<svg viewBox="0 0 40 40">
							<path d="M21 4v22h13z" fill="rgba(255,255,255,.92)" />
							<path d="M19 9v17H9z" fill="rgba(204,251,241,.85)" />
							<rect x="19.2" y="3" width="1.6" height="24" rx=".8" fill="#e2e8f0" />
							<path d="M5 28h30l-4.5 6.5h-21z" fill="#ffffff" />
							<path d="M5 28h30l-1 1.5H6z" fill="#f59e0b" />
						</svg>
					</span>
					<div className={classes.ilustra} aria-hidden="true">
						{CURRICULOS.map((c, i) => (
							<div
								key={i}
								className={classes.curriculo}
								style={{
									left: c.x,
									top: c.y,
									transform: `rotate(${c.r}deg)`,
									animationDelay: `${0.1 + i * 0.12}s`,
									zIndex: c.selo ? 1 : 0,
								}}
							>
								<span className={classes.cvFoto} style={{ background: c.cor }} />
								<span className={classes.cvLinha} style={{ width: "80%", background: "#cfdedb" }} />
								<span className={classes.cvLinha} style={{ width: "55%" }} />
								<span className={classes.cvLinha} style={{ width: "90%" }} />
								<span className={classes.cvLinha} style={{ width: "65%" }} />
								{c.selo && (
									<span className={classes.cvSelo}>
										<HowToReg />
									</span>
								)}
							</div>
						))}
						<LupaRH className={classes.lupaHero} cor="#5eead4" />
					</div>

					<div className={classes.heroTexto}>
						<span className={classes.heroPilula}>PAINEL DE RECRUTAMENTO</span>
						<div className={classes.heroTitulo}>
							Olá, <span>{primeiroNome}</span>
						</div>
						<div className={classes.heroSub}>
							Candidatos, triagem da IA e entrevistas — tudo do seu RH em um só lugar
						</div>
					</div>

					<Box className={classes.filtros}>
						<PeriodFilter value={period} onChange={setPeriod} />

						<Autocomplete
							size="small"
							className={classes.formControl}
							options={opcoesConexao}
							getOptionLabel={o => (o && o.name) || ""}
							getOptionSelected={(o, v) => String(o.id) === String(v.id)}
							value={acharOpcao(opcoesConexao, connectionFilter)}
							onChange={(e, novo) => setConnectionFilter(novo ? novo.id : "all")}
							inputValue={buscaConexao}
							onInputChange={(e, texto, motivo) =>
								setBuscaConexao(motivo === "input" ? texto : "")
							}
							disableClearable
							forcePopupIcon={false}
							openOnFocus
							blurOnSelect
							classes={{
								popper: classes.popupFiltro,
								paper: classes.paperFiltro,
								option: classes.opcaoFiltro,
								noOptions: classes.semResultado,
							}}
							noOptionsText="Nenhum resultado"
							ListboxProps={{ className: classes.listaFiltro }}
							renderInput={params => (
								<TextField
									{...params}
									variant="outlined"
									placeholder={acharOpcao(opcoesConexao, connectionFilter).name}
									autoComplete="off"
									InputProps={{
										...params.InputProps,
										startAdornment: <Search className={classes.iconeBusca} />,
									}}
									inputProps={{ ...params.inputProps, autoComplete: "off" }}
								/>
							)}
						/>

						{(user.profile === "admin" || user.profile === "operador") && (
							<Autocomplete
								size="small"
								className={classes.formControl}
								options={opcoesAtendente}
								getOptionLabel={o => (o && o.name) || ""}
								getOptionSelected={(o, v) => String(o.id) === String(v.id)}
								value={acharOpcao(opcoesAtendente, userFilter)}
								onChange={(e, novo) => setUserFilter(novo ? novo.id : "all")}
								inputValue={buscaAtendente}
								onInputChange={(e, texto, motivo) =>
									setBuscaAtendente(motivo === "input" ? texto : "")
								}
								disableClearable
								forcePopupIcon={false}
								openOnFocus
								blurOnSelect
								classes={{
									popper: classes.popupFiltro,
									paper: classes.paperFiltro,
									option: classes.opcaoFiltro,
									noOptions: classes.semResultado,
								}}
								noOptionsText="Nenhum resultado"
								ListboxProps={{ className: classes.listaFiltro }}
								renderInput={params => (
									<TextField
										{...params}
										variant="outlined"
										placeholder={acharOpcao(opcoesAtendente, userFilter).name}
										autoComplete="off"
										InputProps={{
											...params.InputProps,
											startAdornment: <Search className={classes.iconeBusca} />,
										}}
										inputProps={{ ...params.inputProps, autoComplete: "off" }}
									/>
								)}
							/>
						)}

						<Button
							variant="contained"
							disableElevation
							className={classes.clearBtn}
							startIcon={<BackspaceOutlined fontSize="small" />}
							onClick={handleClear}
						>
							Limpar
						</Button>
					</Box>
				</div>

				<Typography className={classes.tituloSecao}>Funil de recrutamento</Typography>
				<div className={classes.funil}>
					{funil.map((et, i) => {
						const Icone = et.icon
						const maior = Math.max(...funil.map(f => Number(f.valor) || 0), 1)
						return (
							<div key={et.nome} className={classes.etapa}>
								<div className={classes.etapaTopo}>
									<span
										className={classes.etapaIcone}
										style={{ background: hexParaRgba(et.cor, escuro ? 0.16 : 0.1), color: et.cor }}
									>
										<Icone />
									</span>
									<span className={classes.etapaNumero} style={{ color: et.cor }}>
										{loading ? "–" : <ContadorAnimado valor={et.valor} />}
									</span>
								</div>
								<span className={classes.etapaNome}>{et.nome}</span>
								<span className={classes.etapaDesc}>{et.desc}</span>
								<div className={classes.etapaTrilha}>
									<div
										className={classes.etapaBarra}
										style={{
											width: loading ? 0 : `${Math.max(((Number(et.valor) || 0) / maior) * 100, 3)}%`,
											background: et.cor,
										}}
									/>
								</div>
								{i < funil.length - 1 && <ChevronRight className={classes.etapaSeta} />}
							</div>
						)
					})}
				</div>

				{[
					{ titulo: "Entrevistas", itens: cardsEntrevistas, colunas: 3 },
					{ titulo: "Candidatos no WhatsApp", itens: cardsAtendimento, colunas: 3 },
					{ titulo: "Recrutadora IA", itens: cardsIA, colunas: 3 },
				].map(bloco => (
					<div key={bloco.titulo}>
						<Typography className={classes.tituloSecao}>{bloco.titulo}</Typography>
						<Grid container spacing={3} className={classes.cardGrid}>
							{bloco.itens.map((card, idx) => {
								const Icon = card.icon
								const cor = card.destaque ? "#ffffff" : card.color
								return (
									<Grid item xs={12} sm={6} md={bloco.colunas} key={idx}>
										<Paper
											className={`${classes.statCard} ${card.destaque ? classes.cardDestaque : ""}`}
											variant="outlined"
											style={{
												"--cor": card.color,
												"--brilho": hexParaRgba(card.color, escuro ? 0.16 : 0.08),
												animationDelay: `${idx * 0.06}s`,
											}}
										>
											<div className={classes.cardHeader}>
												<Typography className={classes.cardTitle}>
													{card.title}
												</Typography>
												<Box
													className={classes.cardIconBox}
													style={{
														animationDelay: `${idx * 0.5}s`,
														backgroundColor: card.destaque
															? "rgba(255,255,255,.2)"
															: hexParaRgba(card.color, escuro ? 0.14 : 0.1),
														border: `1px solid ${card.destaque ? "rgba(255,255,255,.3)" : hexParaRgba(card.color, 0.3)}`,
													}}
												>
													<Icon style={{ color: cor, fontSize: 20 }} />
												</Box>
											</div>
											<Typography className={classes.cardNumber} style={{ color: cor }}>
												{loading ? (
													<CircularProgress size={28} style={{ color: cor }} />
												) : (
													<ContadorAnimado valor={card.count} />
												)}
											</Typography>
											<Typography className={classes.cardLabel}>
												{card.label}
											</Typography>
											<Typography className={classes.cardFooter}>
												{card.footer}
											</Typography>
											<Onda
												atraso={-idx * 2.3}
												cor={
													card.destaque
														? "rgba(255,255,255,.12)"
														: hexParaRgba(card.color, escuro ? 0.07 : 0.06)
												}
											/>
										</Paper>
									</Grid>
								)
							})}
						</Grid>
					</div>
				))}

				<Typography className={classes.tituloSecao}>Movimento</Typography>
				<Grid container spacing={3} alignItems="flex-start">
					{temVagas && (
						<Grid item xs={12} md={5}>
							<Paper className={classes.rankPaper} variant="outlined">
								<Typography className={classes.paperTitle}>
									Entrevistas por vaga
								</Typography>
								{ent.porVaga.map(v => {
									const maior = ent.porVaga[0]?.total || 1
									return (
										<div key={v.vaga} className={classes.linhaBanco}>
											<span className={classes.nomeBanco}>{v.vaga}</span>
											<div className={classes.trilhaBarra}>
												<div
													className={classes.barra}
													style={{ width: `${Math.max((v.total / maior) * 100, 4)}%` }}
												/>
											</div>
											<span className={classes.valorBanco}>{v.total}</span>
										</div>
									)
								})}
							</Paper>
						</Grid>
					)}

					<Grid item xs={12} md={temVagas ? 7 : 12}>
						<Paper className={classes.chartPaper} variant="outlined">
							<Chart
								serie={resumo?.serie}
								titulo={tituloGrafico}
								loading={loading}
							/>
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</div>
	)
}

export default Dashboard
